import type { MelodyEntityNote } from '@tunasong/schemas'

/**
 * Ghost options (melody toolkit M4, step 4.2; spec §6): ideas shown over the roll as ghost notes, cycled, auditioned,
 * then kept or discarded. Generic: Suggest a melody uses it now, and the selection transforms (M6) and Keep going (M7)
 * will too. Ghosts are local to the person auditioning them and are never written to the document until `Keep`.
 *
 * Pure, so every rule is tested without a screen.
 */

export interface GhostOption {
  notes: MelodyEntityNote[]
  /** One line describing the option; empty when it has none, and then only its position is shown */
  label: string
  /**
   * A selection transform's option (melody toolkit M6): `notes` is the whole melody after Keep, and these are the
   * notes it writes, by index. Only they are drawn as ghosts, and after Keep they are the selection (spec §6, 4a).
   * Absent for a whole-line option such as Suggest a melody's.
   */
  selection?: number[]
}

/** What a request for options came back with */
export type GhostResult =
  | { kind: 'options'; options: GhostOption[] }
  /** `isNotCounted` only when the server said it didn't charge (spec §6: never inferred from an error) */
  | { kind: 'failed'; line: string; isNotCounted: boolean }

export type GhostState =
  | { status: 'idle' }
  | { status: 'writing' }
  | { status: 'showing'; options: GhostOption[]; index: number }
  | { status: 'failed'; line: string; isNotCounted: boolean }

export type GhostAction =
  | { type: 'start' }
  | { type: 'resolve'; result: GhostResult }
  | { type: 'next' }
  | { type: 'previous' }
  | { type: 'close' }

export const idleGhost: GhostState = { status: 'idle' }

/** Spec §8, §11: offline, AI options are disabled with this reason */
export const needsConnectionReason = 'Needs a connection.'

/** The plain failure line: for a request that failed with nothing to say, or an answer with no options */
export const plainGhostFailureLine = "Couldn't write a melody. Try again."

export const ghostReducer = (state: GhostState, action: GhostAction): GhostState => {
  switch (action.type) {
    case 'start':
      return { status: 'writing' }
    case 'resolve': {
      /** An answer that arrives after Discard, or after another start, is not shown */
      if (state.status !== 'writing') {
        return state
      }
      const { result } = action
      if (result.kind === 'failed') {
        return { status: 'failed', line: result.line, isNotCounted: result.isNotCounted }
      }
      return result.options.length > 0
        ? { status: 'showing', options: result.options, index: 0 }
        : { status: 'failed', line: plainGhostFailureLine, isNotCounted: false }
    }
    case 'next':
    case 'previous': {
      if (state.status !== 'showing') {
        return state
      }
      const step = action.type === 'next' ? 1 : -1
      const count = state.options.length
      return { ...state, index: (state.index + step + count) % count }
    }
    case 'close':
      return idleGhost
  }
}

/** "Option 2 of 3", the position the bar shows */
export const ghostPosition = (state: Extract<GhostState, { status: 'showing' }>) =>
  `Option ${state.index + 1} of ${state.options.length}`

/** Joni (2026-10-31): one option has no position worth giving, so it is named by its label alone, when it has one */
export const isPositionShown = (state: Extract<GhostState, { status: 'showing' }>) =>
  state.options.length > 1 || !state.options[state.index]?.label

/** Spec §10: the polite live region's text, "Option 1 of 3: {label}"; empty when no options are showing */
export const ghostAnnouncement = (state: GhostState): string => {
  if (state.status !== 'showing') {
    return ''
  }
  const label = state.options[state.index]?.label ?? ''
  if (!isPositionShown(state)) {
    return label
  }
  return label ? `${ghostPosition(state)}: ${label}` : ghostPosition(state)
}
